<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12" md="8">
                <v-card flat>
                    <v-card-text>
                        <v-form>
                            <v-row>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            label="Name"
                                            v-model="$v.cognitoUser.attributes.name.$model"
                                            :error-messages="nameError"
                                            error-count=2
                                            required
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            label="Family Name"
                                            v-model="$v.cognitoUser.attributes.family_name.$model"
                                            :error-messages="lastNameError"
                                            error-count=2
                                            required
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3" pa-1 ma-1>
                                    <datePicker
                                            :errorFn = "dobError"
                                            labelProp="DOB"
                                            :valueProp="cognitoUser.attributes.birthdate"
                                            @dateValue="$v.cognitoUser.attributes.birthdate.$model = $event"
                                    ></datePicker>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="8" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.address.address_line_1.$model"
                                            label="Address"
                                            :error-messages="addressError"
                                            error-count=2
                                            required
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.address.address_line_2.$model"
                                            label="Apt #"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.address.city.$model"
                                            label="City"
                                            :error-messages="addressCityError"
                                            error-count=2
                                            required
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <usStatesSelect
                                            :state="address.state"
                                            :error-fn="addressStateError"
                                            :required="true"
                                            @state-selected="$v.address.state.$model = $event"
                                    ></usStatesSelect>
                                </v-col>
                                <v-col cols="12" md="3" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.address.zip_code.$model"
                                            label="Zip Code"
                                            :error-messages="addressZipError"
                                            error-count=2
                                            required
                                            v-mask="masks.zipCode"
                                    >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.cognitoUser.attributes.email.$model"
                                            label="Email"
                                            :error-messages="emailError"
                                            error-count=2
                                            required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4" pa-1 ma-1>
                                    <v-text-field
                                            outlined
                                            v-model="$v.cognitoUser.attributes.phone_number.$model"
                                            label="Phone"
                                            :error-messages="phoneError"
                                            error-count=2
                                            required
                                            v-mask="masks.phoneMask"
                                    >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="3" pa-1 ma-1 v-if="id === undefined">
                                    <v-text-field
                                            outlined
                                            v-model="$v.cognitoUser.username.$model"
                                            label="Username"
                                            :error-messages="usernameError"
                                            error-count=2
                                            required
                                    >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="center">
                                <v-col cols="6" sm="2">
                                    <v-btn color="default" text @click="close">Cancel</v-btn>
                                </v-col>
                                <v-col cols="6" sm="2">
                                    <v-btn color="primary"
                                           :loading="isSaving"
                                           :disabled="isSaving"
                                           @click="save">Save</v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <dialog-alert
                :text="alertText"
                :alert-type="alertType"
                :open="openAlert"
                @alert-closed="alertClosed($event)">
        </dialog-alert>
        <dialog-alert :text="notFoundError"
                      :alert-type="alertTypes.ERROR"
                      :open="openNotFound"
                      @alert-closed="close()">
        </dialog-alert>
    </v-container>
</template>

<script>
    import alertEventBus from '@/eventBus/AlertEventBus';
    import {ALERT_TYPES} from "@/shared/enums/AlertTexts";
    import {WRONG_ID_SUPPLIED}  from "@/shared/enums/AlertTexts";
    import {required, minLength, email, requiredIf, numeric, maxLength} from 'vuelidate/lib/validators';
    import {CognitoUser, Employee} from '@/shared/dtos/Dtos';
    const datePicker = ()=> import( '@/components/common/DatePicker');
    import {ACCT_TYPES} from '@/shared/enums/UserType';
    import {TOKEN} from '@/shared/enums/TokenSeparator';
    import {FORM_STATUS} from '@/shared/enums/FormStatuses';
    import {EMPLOYEE_CLIENT_STATUS} from '@/shared/enums/EntitiesStatus';
    const DialogAlert = ()=> import( '@/components/common/DialogAlert');
    import {formUnsavedMixin} from "@/shared/mixins/FormUnsaved";
    const usStatesSelect = ()=> import('@/components/common/UsStatesSelect');
    import { ALPHA_WITH_SPACES_VALIDATOR, ALPHA_NUMERIC_WITH_SPACES_VALIDATOR } from "@/shared/validators/Validators";
    import {EMPLOYEE_ACTIONS} from "@/shared/enums/Employee";

    export default {
        name: "employeeForm",
        props:['id'],
        mixins:[formUnsavedMixin],
        components:{
            datePicker, DialogAlert, usStatesSelect
        },
        data(){
            return {
                address: {
                    address_line_1: '',
                    address_line_2: '',
                    city: '',
                    state: '',
                    zip_code:''
                },
                cognitoUser: {},
                employee: {},
                isSaving: false,
                masks:{
                    phoneMask: "##########",
                    zipCode: "#####"
                },
                notFoundError: WRONG_ID_SUPPLIED,
                openNotFound: false,
                alertTypes: ALERT_TYPES
            };
        },
        validations: {
            cognitoUser:{
                attributes: {
                    name: {
                        required,
                        minLength: minLength(1),
                        ALPHA_WITH_SPACES_VALIDATOR
                    },
                    family_name: {
                        required,
                        ALPHA_WITH_SPACES_VALIDATOR
                    },
                    birthdate: {
                        required
                    },
                    email: {
                        required,
                        email: email
                    },
                    phone_number: {
                        required
                    },
                },
                username: {
                    required: requiredIf(function() { return this.id === undefined; }),
                    minLength: minLength(6)
                }
            },
            address: {
                address_line_1: {
                    required
                },
                address_line_2: {
                    numeric
                },
                city: {
                    required,
                    ALPHA_NUMERIC_WITH_SPACES_VALIDATOR
                },
                state: {
                    required
                },
                zip_code: {
                    required,
                    numeric,
                    maxLength: maxLength(5)
                }
            }
        },
        computed:{
            nameError(){
                const errors = []
                if (!this.$v.cognitoUser.attributes.name.$dirty) {
                    return errors
                }

                !this.$v.cognitoUser.attributes.name.minLength && errors.push('Name must be at least '+
                    this.$v.cognitoUser.attributes.name.$params.minLength.min +' characters long')
                !this.$v.cognitoUser.attributes.name.required && errors.push('Name is required.')
                !this.$v.cognitoUser.attributes.name.ALPHA_WITH_SPACES_VALIDATOR && errors.push('Name must contains only letters')

                return errors
            },
            lastNameError(){
                const errors = []
                if (!this.$v.cognitoUser.attributes.family_name.$dirty) {
                    return errors
                }

                !this.$v.cognitoUser.attributes.family_name.required && errors.push('Last name is required.')
                !this.$v.cognitoUser.attributes.family_name.ALPHA_WITH_SPACES_VALIDATOR && errors.push('Family name must contains only letters')

                return errors
            },
            dobError(){
                const errors = []
                if (!this.$v.cognitoUser.attributes.birthdate.$dirty) {
                    return errors
                }

                !this.$v.cognitoUser.attributes.birthdate.required && errors.push('DOB is required.')
                return errors
            },
            addressError(){
                const errors = []
                if (!this.$v.address.address_line_1.$dirty) {
                    return errors
                }

                !this.$v.address.address_line_1.required && errors.push('Address is required.')
                return errors
            },
            addressCityError(){
                const errors = []
                if (!this.$v.address.city.$dirty) {
                    return errors
                }

                !this.$v.address.city.required && errors.push('City is required.')
                !this.$v.address.city.ALPHA_NUMERIC_WITH_SPACES_VALIDATOR && errors.push('City must contains only letters and numbers')

                return errors
            },
            addressStateError(){
                const errors = []
                if (!this.$v.address.state.$dirty) {
                    return errors
                }

                !this.$v.address.state.required && errors.push('State is required.')
                return errors
            },
            addressZipError(){
                const errors = []
                if (!this.$v.address.zip_code.$dirty) {
                    return errors
                }

                !this.$v.address.zip_code.required && errors.push('Zip Code is required.')
                !this.$v.address.zip_code.numeric && errors.push('Zip Code must contain only numbers.')

                return errors
            },
            usernameError(){
                const errors = []
                if (!this.$v.cognitoUser.username.$dirty) {
                    return errors
                }

                !this.$v.cognitoUser.username.minLength && errors.push('Username must be at least '+
                    this.$v.cognitoUser.username.$params.minLength.min +' characters long')
                !this.$v.cognitoUser.username.required && errors.push('Username is required.')
                return errors
            },
            emailError(){
                const errors = []
                if (!this.$v.cognitoUser.attributes.email.$dirty) {
                    return errors
                }

                !this.$v.cognitoUser.attributes.email.email && errors.push('A valid email address is required')
                !this.$v.cognitoUser.attributes.email.required && errors.push('Email address is required.')
                return errors
            },
            phoneError(){
                const errors = []
                if (!this.$v.cognitoUser.attributes.phone_number.$dirty) {
                    return errors
                }
                !this.$v.cognitoUser.attributes.phone_number.required && errors.push('Phone number is required.')
                return errors
            }
         },
        methods:{
            async initialize(){
                this.cognitoUser = JSON.parse(JSON.stringify(CognitoUser));
                this.employee = {...Employee};
                this.employee.status = EMPLOYEE_CLIENT_STATUS.UPDATE_REQUIRED.status;

                if(this.id !== undefined){
                    await this.$store.dispatch('getEmployee',{id: this.id});

                    this.employee = this.$store.getters.getEmployee;

                    if(!this.employee.hasOwnProperty('id'))
                    {
                        this.openNotFound = true;
                        return;
                    }
                    else
                    {
                        this.loadCognitoUser();
                    }
                }
            },
            close(){
                this.resetValues();
                this.status = FORM_STATUS.CLOSE;
                this.$router.push({name: 'employeeTable'});
            },
            resetValues(){
                this.$v.$reset();
                this.$store.commit('setEmployee',null);
                this.$store.commit('setCognitoUser',null);
                this.employee = {...Employee};
            },
            async loadCognitoUser(){
                try
                {
                    await this.$store.dispatch('getCognitoUser',{username:this.employee.cognitoUsername});
                    let response = this.$store.getters.getCognitoUser;
                    if(response){
                        this.cognitoUser.username = response.Username;
                        for(let property in response.UserAttributes){
                            this.cognitoUser.attributes[response.UserAttributes[property].Name] = response.UserAttributes[property].Value;
                        }

                        this.cognitoUser.attributes.phone_number = this.cognitoUser.attributes.phone_number.replace(/\+1/,'');
                        this.address = JSON.parse(this.cognitoUser.attributes.address);
                    }
                }
                catch (e)
                {
                    //set alert
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    return;
                }
            },
            async save(){
                this.$v.$touch();
                if(this.$v.$anyError)
                    return;

                this.isSaving = true;
                try
                {
                    /**
                     * Saving the employee
                     */
                    this.employee.cognitoUsername = this.cognitoUser.username;
                    this.employee.name =  this.cognitoUser.attributes.name+' '+this.cognitoUser.attributes.family_name;
                    let address = {
                        address_line_1: this.address.address_line_1,
                        address_line_2: this.address.address_line_2,
                        city:           this.address.city,
                        state:          this.address.state,               // province also valid
                        zip_code:       this.address.zip_code,            // postal_code also valid
                        country:        'United States'
                    };
                    this.employee.address = address;

                    await this.$store.dispatch('setEmployee',this.employee);
                    let response = this.$store.getters.getEmployee;

                    if(response.id || this.id !== undefined)
                    {
                        /**
                         * Creating the cognito user
                         */
                        this.cognitoUser.attributes.address = JSON.stringify(address);
                        let userAuth = this.$store.getters.getUser;

                        if(userAuth){
                            this.cognitoUser.attributes['custom:acctType'] = ACCT_TYPES.EMPLOYEE;
                            this.cognitoUser.update = this.id !== undefined;
                            let newEmployee = JSON.parse(JSON.stringify(this.cognitoUser));
                            newEmployee.attributes.phone_number = '+1'+newEmployee.attributes.phone_number;

                            try
                            {
                                await this.$store.dispatch('setCognitoUser', newEmployee);
                            }
                            catch (e)
                            {
                                await this.$store.dispatch('deleteEmployee', {id: response.id});
                                if(e.response.data.message)
                                    alertEventBus.setAlert({type:'error', msg: e.response.data.message});
                                else
                                    alertEventBus.setAlert({type:'error', msg: 'Something went wrong, please try again later'});

                                return;
                            }
                        }

                        /**
                         * Updating list of Employees
                         */
                        this.$store.commit('setEmployees', []);
                    }
                }
                catch (e){
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    this.isSaving = false;
                    return;
                }

                this.status = FORM_STATUS.SAVE;

                if(this.id === undefined){
                    this.$router.push({name: EMPLOYEE_ACTIONS.ADD_DOCUMENTS.route})
                }else{
                    this.close();
                }
            },
        },
        async created(){
            this.initialize();
        },
    }
</script>

<style>
    .v-chip.white-span span {
        color: white !important;
    }
</style>